/**
 * AppShell — VS Code style layout
 *
 * Structure:
 *   ┌─────────────────────────────────────────┐
 *   │ Top App Bar (logo, search, user menu)   │
 *   ├──┬──────────┬──────────────────────────┤
 *   │  │          │                          │
 *   │A │  Side    │     Map Canvas           │
 *   │c │  Panel   │                          │
 *   │t │          │                          │
 *   │i │          │                          │
 *   │v │          │                          │
 *   │  │          │                          │
 *   ├──┴──────────┴──────────────────────────┤
 *   │ Status Bar (CRS, coords, scale)        │
 *   └─────────────────────────────────────────┘
 */
import React, { useState, useCallback, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../theme';
import { useGeoveraTheme } from '../../theme/ThemeContext';
import { useAppSelector } from '../../store/hooks';
import TopAppBar from './TopAppBar';
import ActivityBar from './ActivityBar';
import SidePanel from './SidePanel';
import StatusBar from './StatusBar';
import NotificationHost from '../NotificationHost';

export type ActivityId =
  | 'layers'
  | 'symbology'
  | 'analysis'
  | 'edit'
  | 'drawing'
  | 'measure'
  | 'survey' // Surveyors: COGO, traverse, control, stakeout
  | 'design' // Civil/Geo Engineers: Alignments, profiles, earthworks, grading
  | 'terrain' // Terrain, 3D, contours, slope, viewshed, interpolation
  | 'field' // Field collection, forms, GNSS, offline
  | 'bookmarks'
  | 'search'
  | 'print'
  | 'crs'
  | null;

interface AppShellProps {
  /** Map canvas element */
  children: ReactNode;
  /** Initial active activity */
  initialActivity?: ActivityId;
}

const AppShell: React.FC<AppShellProps> = ({ children, initialActivity = 'layers' }) => {
  const { palette } = useGeoveraTheme();
  const isSimple = useAppSelector((state) => state.ui.isNonTechnicalMode);
  const [activeActivity, setActiveActivity] = useState<ActivityId>(initialActivity);
  const [panelWidth, setPanelWidth] = useState(tokens.layout.sidePanelWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Keep shell activity in sync when toggling Simple ↔ Advanced
  React.useEffect(() => {
    if (isSimple) {
      setActiveActivity(null);
    } else if (activeActivity === null) {
      setActiveActivity(initialActivity ?? 'layers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to mode flips
  }, [isSimple]);

  const handleActivityChange = useCallback((id: ActivityId) => {
    // Click same icon to toggle panel closed
    setActiveActivity((prev) => (prev === id ? null : id));
  }, []);

  // Header tool buttons can open activity panels via custom events
  React.useEffect(() => {
    const onOpenActivity = (e: Event) => {
      const id = (e as CustomEvent<{ id?: ActivityId }>).detail?.id;
      if (id) setActiveActivity(id);
    };
    window.addEventListener('geovera:open-activity', onOpenActivity as EventListener);
    return () => {
      window.removeEventListener('geovera:open-activity', onOpenActivity as EventListener);
    };
  }, []);

  // Handle panel resize via drag
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = panelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(
          200,
          Math.min(tokens.layout.sidePanelMaxWidth, startWidth + delta)
        );
        setPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [panelWidth]
  );

  const isPanelOpen = activeActivity !== null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: palette.bg.base,
        color: palette.text.primary,
        overflow: 'hidden',
        userSelect: isResizing ? 'none' : 'auto',
        cursor: isResizing ? 'col-resize' : 'default',
      }}
    >
      {/* ── TOP APP BAR ─────────────────────────────────────── */}
      <TopAppBar />

      {/* ── MAIN AREA: Activity Bar | Side Panel | Map ───── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {!isSimple && (
          <ActivityBar activeActivity={activeActivity} onActivityChange={handleActivityChange} />
        )}

        {!isSimple && isPanelOpen && (
          <>
            <SidePanel
              activity={activeActivity}
              width={panelWidth}
              onClose={() => setActiveActivity(null)}
            />
            {/* Resize handle */}
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                width: 4,
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                transition: `background-color ${tokens.animation.fast}`,
                flexShrink: 0,
                position: 'relative',
                display: { xs: 'none', sm: 'block' },
                '&:hover, &:active': {
                  backgroundColor: palette.accent.primary,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: '0 -2px',
                },
              }}
            />
          </>
        )}

        {/* Map canvas area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            minWidth: 0,
            backgroundColor: palette.bg.surface,
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* ── STATUS BAR ──────────────────────────────────────── */}
      <StatusBar />

      {/* Global toasts for tool success/error feedback */}
      <NotificationHost />
    </Box>
  );
};

export default AppShell;
